/*
 * skio-gift-guard.js — keeps bundle gifts honest for the life of the cart.
 *
 * The Skio bundle picker adds the free gift as a plain $0 cart line. Shopify has no idea the
 * gift depends on the bundle, so before this file a customer could add the Three Month Ritual,
 * take the free tea, drop back to one bag in the drawer and keep the tea. They could also step
 * the tea's own quantity up and take five.
 *
 * Every gift line the picker adds now carries a hidden `_skio_gift` property:
 *   { p: <product id the gift depends on>, min: <units required>, sub: <bool>, q: <gift qty> }
 * After any cart mutation this reads /cart.js, checks each gift against its rule and removes
 * the gift when the rule no longer holds. Gift lines are also capped at their own quantity.
 *
 * Gifts already in carts from before this shipped have no property. For those, the fallback
 * rules in window.SKIO_GIFT_RULES (set in layout/theme.liquid) apply: the gift stays if ANY
 * listed rule is satisfied.
 *
 * It also puts a gift back. A SKIO_GIFT_RULES entry marked autoAdd is an offer: when the cart
 * qualifies again (she drops to two bags, then steps back up to three in the drawer) and the
 * gift is missing, it is re-added as a tagged $0 line. The one exception is a gift she removed
 * herself while still qualifying; that is remembered for this cart (localStorage, keyed by
 * cart token) until the cart stops qualifying, she re-adds it, or the cart empties.
 *
 * It also hides the quantity stepper on gift lines in the Kaching drawer (see markGiftLines
 * below). The /cart page does the same server-side in snippets/cart-line-items.liquid.
 */
(function () {
  'use strict';

  var PROP = '_skio_gift';
  var root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
  if (root[root.length - 1] !== '/') root += '/';

  var busy = false;
  var queued = false;
  var timer = null;

  function log() {
    if (window.SKIO_GIFT_DEBUG && window.console) console.log.apply(console, ['[skio-gift-guard]'].concat([].slice.call(arguments)));
  }

  function parseRule(item) {
    var raw = item.properties && item.properties[PROP];
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  /* Units of product `p` in the cart, excluding gift lines, optionally subscription lines only. */
  function countQualifying(items, rule) {
    var n = 0;
    items.forEach(function (it) {
      if (it.properties && it.properties[PROP]) return;
      if (String(it.product_id) !== String(rule.p)) return;
      if (rule.sub && !it.selling_plan_allocation) return;
      n += it.quantity;
    });
    return n;
  }

  function ruleHolds(items, rule) {
    return countQualifying(items, rule) >= (rule.min || 1);
  }

  /* Untagged gift: keep it if any fallback rule for that variant holds. */
  function fallbackHolds(items, item) {
    var rules = window.SKIO_GIFT_RULES || [];
    var matched = false;
    var ok = false;
    rules.forEach(function (r) {
      if (String(r.variantId) !== String(item.variant_id)) return;
      matched = true;
      (r.any || []).forEach(function (rule) { if (ruleHolds(items, rule)) ok = true; });
    });
    /* A line we have no rule for is not ours to touch. */
    return matched ? ok : true;
  }

  function offers() {
    return (window.SKIO_GIFT_RULES || []).filter(function (r) { return r.autoAdd && r.variantId; });
  }

  function offerFor(variantId) {
    var found = null;
    offers().forEach(function (r) { if (String(r.variantId) === String(variantId)) found = r; });
    return found;
  }

  /* The first rule of an offer the cart currently satisfies, or null. */
  function holdingRule(items, offer) {
    var hit = null;
    (offer.any || []).forEach(function (rule) { if (!hit && ruleHolds(items, rule)) hit = rule; });
    return hit;
  }

  function isGift(item) {
    if (item.properties && item.properties[PROP]) return true;
    return (window.SKIO_GIFT_RULES || []).some(function (r) {
      return String(r.variantId) === String(item.variant_id);
    });
  }

  /* Decide the target quantity for each gift line. null = leave alone. */
  function plan(cart) {
    var items = cart.items || [];
    var changes = [];
    var seenVariant = {};

    items.forEach(function (item) {
      if (!isGift(item)) return;
      var rule = parseRule(item);
      var holds = rule ? ruleHolds(items, rule) : fallbackHolds(items, item);
      /*
       * An auto-add offer is one promise across its rules: tea tagged for 3 Mineral Mist stays
       * if 2 Wild Nectar still qualify, instead of being removed and re-added a moment later.
       */
      if (!holds) {
        var offer = offerFor(item.variant_id);
        if (offer && holdingRule(items, offer)) holds = true;
      }
      var target;
      if (!holds) {
        target = 0;
      } else if (seenVariant[item.variant_id]) {
        /* Second line of the same gift (e.g. picker run twice): drop it. */
        target = 0;
      } else {
        /* Tagged gifts cap at the quantity the picker gave them; untagged legacy gifts at 1. */
        target = (rule && rule.q) || 1;
      }
      seenVariant[item.variant_id] = true;
      if (target !== item.quantity) {
        changes.push({ key: item.key, quantity: target, reason: !holds ? 'rule not met' : (target === 0 ? 'duplicate' : 'quantity capped') });
      }
    });
    return changes;
  }

  /*
   * With Kaching's "show quantity selector" on, the drawer gives every line a -/+ stepper,
   * the gift included. The cap above would snap a bumped gift back to its own quantity, but
   * the customer still sees a stepper that looks broken. Kaching has no idea the line is a
   * gift (the picker added it, not Kaching), so tag the line ourselves: data-skio-gift on
   * .kaching-cart-item, matched by line key, and the rule below hides the stepper there.
   * Kaching re-renders lines on every cart change, so the tag is re-applied from the gift
   * keys of the last cart read whenever the drawer's DOM changes. Lines of a SKIO_GIFT_RULES
   * variant are hidden by line-key prefix too, which covers the moment between a gift being
   * added and the next cart read.
   */
  (function () {
    var sel = ['.kaching-cart-item[data-skio-gift] .kaching-cart-item__quantity-controls'];
    (window.SKIO_GIFT_RULES || []).forEach(function (r) {
      if (/^\d+$/.test(String(r.variantId))) {
        sel.push('.kaching-cart-item[data-line-key^="' + r.variantId + ':"] .kaching-cart-item__quantity-controls');
      }
    });
    var style = document.createElement('style');
    style.setAttribute('data-skio-gift-guard', '');
    style.textContent = sel.join(',\n') + ' { display: none !important; }';
    document.head.appendChild(style);
  })();

  var giftKeys = {};
  var hasGifts = false;

  function markGiftLines() {
    var nodes = document.querySelectorAll('.kaching-cart-item[data-line-key]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var gift = !!giftKeys[el.getAttribute('data-line-key')];
      if (gift && !el.hasAttribute('data-skio-gift')) el.setAttribute('data-skio-gift', '');
      else if (!gift && el.hasAttribute('data-skio-gift')) el.removeAttribute('data-skio-gift');
    }
  }

  function rememberGifts(cart) {
    giftKeys = {};
    hasGifts = false;
    (cart.items || []).forEach(function (item) {
      if (isGift(item)) { giftKeys[item.key] = true; hasGifts = true; }
    });
    markGiftLines();
  }

  var markQueued = false;

  function inDrawer(node) {
    var el = node && (node.nodeType === 1 ? node : node.parentElement);
    return !!(el && el.closest && el.closest('kaching-cart, kaching-cart-drawer'));
  }

  if (typeof MutationObserver === 'function') {
    new MutationObserver(function (records) {
      /*
       * Kaching's drawer changes the cart through the Storefront API with its own untouched
       * copy of fetch, so the fetch/XHR patches below never see it, and on a slow connection
       * its request outlasts the click fallback: the guard read the cart at 2 bags, the 3rd
       * landed afterwards and nothing re-checked, so the tea never came back. The drawer
       * re-renders once the new cart is in, so a re-render is the reliable signal. Our own
       * refresh re-renders it too, which costs one extra read and then settles.
       */
      for (var i = 0; i < records.length; i++) {
        if (inDrawer(records[i].target)) { schedule(400); break; }
      }
      if (!hasGifts || markQueued) return;
      markQueued = true;
      (window.requestAnimationFrame || setTimeout)(function () {
        markQueued = false;
        markGiftLines();
      });
    }).observe(document.documentElement, { childList: true, characterData: true, subtree: true });
  }

  /*
   * Per-cart memory: which offer gifts were in the cart at the last read (had) and which ones
   * she removed herself while qualifying (declined). A new cart token starts clean.
   */
  var MEM_KEY = 'skio-gift-guard';
  var addFailed = {};

  function loadMem(token) {
    try {
      var m = JSON.parse(window.localStorage.getItem(MEM_KEY) || 'null');
      if (m && m.token === token) {
        m.had = m.had || {};
        m.declined = m.declined || {};
        return m;
      }
    } catch (e) { /* storage blocked: behave as a fresh cart */ }
    return { token: token, had: {}, declined: {} };
  }

  function saveMem(mem) {
    try { window.localStorage.setItem(MEM_KEY, JSON.stringify(mem)); } catch (e) { /* ignore */ }
  }

  function giftVariantsIn(items) {
    var present = {};
    items.forEach(function (it) { if (isGift(it)) present[String(it.variant_id)] = true; });
    return present;
  }

  /* Offer gifts to add back: qualifying, missing, not declined, not already failed this page. */
  function planAdds(items, present, mem) {
    var adds = [];
    offers().forEach(function (offer) {
      var vid = String(offer.variantId);
      if (present[vid] || mem.declined[vid] || addFailed[vid]) return;
      var rule = holdingRule(items, offer);
      if (!rule) return;
      var q = offer.q || 1;
      adds.push({
        id: offer.variantId,
        quantity: q,
        properties: { _skio_gift: JSON.stringify({ p: rule.p, min: rule.min || 1, sub: !!rule.sub, q: q }) }
      });
    });
    return adds;
  }

  function addLines(lines) {
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: lines }),
      __skioGuard: true
    }).then(function (r) {
      if (!r.ok) throw new Error('cart/add ' + r.status);
      return r.json();
    });
  }

  function getCart() {
    return fetch(root + 'cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' }, __skioGuard: true })
      .then(function (r) { return r.json(); });
  }

  function change(key, quantity) {
    return fetch(root + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
      __skioGuard: true
    }).then(function (r) { return r.json(); });
  }

  function refreshUi(changes) {
    document.dispatchEvent(new CustomEvent('skio:gift-guard', { detail: { changes: changes } }));
    var api = window.kachingCartApi;
    if (api && typeof api.refreshCart === 'function') {
      try { api.refreshCart(); } catch (e) { /* drawer will catch up on next open */ }
    }
    /* The theme's own cart page renders server-side, so it has to be reloaded to agree. */
    var cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
    if (location.pathname.replace(/\/$/, '') === cartUrl.replace(/\/$/, '')) {
      location.reload();
    }
  }

  function run() {
    if (busy) { queued = true; return; }
    busy = true;
    var mem = null;
    getCart()
      .then(function (cart) {
        rememberGifts(cart);
        var items = cart.items || [];
        mem = loadMem(cart.token);
        /* Shopify keeps the cart token when a cart is emptied, so an empty cart starts over. */
        if (!items.length) mem.declined = {};
        var present = giftVariantsIn(items);

        offers().forEach(function (offer) {
          var vid = String(offer.variantId);
          if (!holdingRule(items, offer)) {
            /* Dropped below the offer: a later step back up earns the gift again. */
            delete mem.declined[vid];
          } else if (present[vid]) {
            /* Back in the cart (the picker, or our own add): auto-add is on again. */
            delete mem.declined[vid];
          } else if (mem.had[vid] && holdingRule(items, offer)) {
            /* It was here, she still qualifies, and we did not take it: she removed it. */
            mem.declined[vid] = true;
            log('gift removed by customer, not re-adding', vid);
          }
        });

        var changes = plan(cart);
        /* What is left after removals (a dropped duplicate leaves its twin in place). */
        present = {};
        items.forEach(function (it) {
          var dropped = changes.some(function (c) { return c.key === it.key && c.quantity === 0; });
          if (!dropped && isGift(it)) present[String(it.variant_id)] = true;
        });
        var adds = planAdds(items, present, mem);

        if (!changes.length && !adds.length) {
          mem.had = present;
          saveMem(mem);
          return null;
        }
        log('applying', changes, 'adding', adds);
        return changes.reduce(function (p, c) {
          return p.then(function () { return change(c.key, c.quantity); });
        }, Promise.resolve())
          .then(function () {
            if (!adds.length) return;
            return addLines(adds).then(function () {
              adds.forEach(function (a) { present[String(a.id)] = true; });
            }, function (err) {
              /* Sold out or rejected: do not retry on every cart change of this page view. */
              adds.forEach(function (a) { addFailed[String(a.id)] = true; });
              log('re-add failed', err);
            });
          })
          .then(function () {
            mem.had = present;
            saveMem(mem);
            return changes.concat(adds.map(function (a) { return { id: a.id, quantity: a.quantity, reason: 'offer re-added' }; }));
          });
      })
      .then(function (changes) { if (changes && changes.length) refreshUi(changes); })
      .catch(function (err) { log('error', err); })
      .then(function () {
        busy = false;
        if (queued) { queued = false; schedule(120); }
      });
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(run, delay == null ? 250 : delay);
  }

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;

  /*
   * Watch every cart mutation from this page: the theme, Kaching's drawer, Instant, anything.
   * fetch and XHR are both patched because the drawer and the theme use different ones.
   * Our own requests carry __skioGuard so they do not re-trigger.
   */
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var ours = init && init.__skioGuard;
      var p = nativeFetch.apply(this, arguments);
      if (!ours && CART_MUTATION.test(url)) {
        p.then(function () { schedule(); }, function () { schedule(); });
      }
      return p;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_MUTATION.test(String(url))) {
      this.addEventListener('loadend', function () { schedule(); });
    }
    return nativeOpen.apply(this, arguments);
  };

  /* Belt and braces for a drawer that grabbed fetch before we patched it. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest('kaching-cart, [data-cart-drawer], form[action*="/cart"], .cart, [class*="cart"]')) {
      schedule(1200);
    }
  }, true);

  document.addEventListener('theme:cart:change', function () { schedule(); });
  window.addEventListener('pageshow', function () { schedule(50); });
  schedule(50);
})();